import React, {useState} from "react";

import {Box} from '@mui/material';

import "../App.css"
import {useAppCtx} from "../app/AppCtx";
import {useCodeCtx} from "../app/CodeCtx";
import {PageHeader} from "./PageHeader";
import {CxGrid} from "../cmx/CxGrid";
import {CxTextField, CxCheckBox, CxCancelSaveButton, CxMetaInfo} from "../cmx/CxForm";
import {CxText} from "../cmx/CxTypography"
import {CxCodeList} from "../cmx/CxCode";
import {CxFormFeedback} from "../cmx/CxFeedback";
import {Code} from "../domain/Code";
import {initialCall, runningCall} from "../gateway/CallService";
import {codeCreate, codeUpdate, codeOrderUpdate} from "../gateway/CodeService";
import {ValidationUtil} from "../lib/ValidationUtil";
import {CxIconButton} from "../cmx/CxButton";

// --------------------------------------------------------------------------------------------------------------------
// RenderCode
// --------------------------------------------------------------------------------------------------------------------

const RenderCode = (cfg, items, item=null) => {
    const isUpdate = item !== null;

    const {handleAction, currentReturnTo} = useAppCtx();
    const {code, loadCodes} = useCodeCtx();

    let initialValue = null;
    if (isUpdate) {
        initialValue = item;
    } else {
        const key = cfg.isMainCode ? code.generateNextMainKey(cfg.code) : code.generateNextSubKey(cfg.code);
        const order =  items.length;
        initialValue = Code.getFormValues(key, order);
    }

    const [formValues, setFormValues] = useState(initialValue);
    const [formErrors, setFormErrors] = useState(Code.getFormErrors());
    const [call, setCall] = useState(initialCall())


    //
    // handlers
    //
    const handleChange = (fieldOrEvent) => {
        // For TextField or CheckBox
        const { name, value, type, checked} = fieldOrEvent.target;

        if (type === "checkbox") {                                  // For CheckBox: Convert checked state to 0/1
            setFormValues((prev) => ({ ...prev, [name]: checked ? 1 : 0 }));
        } else {                                                    // For TextField: Sanitize and update value
            setFormValues((prev) => ({ ...prev, [name]: value }));
        }
        setFormErrors((prev) => ({ ...prev, [name]: false }));
    };

    const handleSubmit = async (action) => {
        if (action === "cancel") {
            handleAction(currentReturnTo)
        }

        if (action === "save") {
            // validate
            const errorsCopy = { ...formErrors }; // Create a copy of the errors object
            const isValid = validate(formValues, errorsCopy);

            if (!isValid) {
                setFormErrors(errorsCopy); // Update errors in state
                return
            }
            // create or update code entity
            let responseCall = null;
            setCall(runningCall);
            if (isUpdate) {
                 responseCall = await codeUpdate(Code.mapFormToDb(formValues));
            } else {
                responseCall = await codeCreate(Code.mapFormToDb(formValues))
            }
            if (responseCall.isError) {
                setCall(responseCall)
                return
            } else {
                loadCodes();
            }
            // return to
            handleAction(currentReturnTo)
        }
    };

    //
    // validation
    //
    function validate(formValues, formErrors) {
        // remove leading and trailing blanks on all input fields
        ValidationUtil.trimTextFields(formValues)

        // validate required fields
        let isValid = ValidationUtil.required(formValues, formErrors, Code.getFormRequiredFields(cfg.showQuantity))

        // validate quantity field
        let isValidNumber = ValidationUtil.number(formValues, formErrors, 'quantity')

        // return the final validation status
        return (isValid && isValidNumber);
    }



    //
    // form inputs
    //
    const formTextField = (field, label, maxLength, numericOnly=false)  => {
        return <CxTextField field={field} label={label} maxLength={maxLength} numericOnly={numericOnly}
                            handleChange={handleChange} formValues={formValues} formErrors={formErrors} />
    }

    const formCheckBox = (field, label) => {
        return <CxCheckBox field={field} label={label}
                           handleChange={handleChange} formValues={formValues} formErrors={formErrors} />
    }

    const formButtons = () => {
        return <CxCancelSaveButton handleSubmit={handleSubmit} />
    }

    return (
        <>
            <CxFormFeedback call={call} />

            <CxGrid size="lg">{ formTextField("value", "Bezeichnung", 255) }</CxGrid>
            <CxGrid size="lg" />

            { cfg.showRef &&
                <>
                    <CxGrid size="lg">{ formTextField("ref", "SORBA Nummer", 32) }</CxGrid>
                    <CxGrid size="lg" />
                </>
            }

            { cfg.showQuantity &&
                <>
                    <CxGrid size="lg">{ formTextField("quantity", "Anzahl", 6, true) }</CxGrid>
                    <CxGrid size="lg" />
                </>
            }

            <CxGrid size="lg">{ formCheckBox("active", "Aktiv")}</CxGrid>
            <CxGrid size="lg" />

            { cfg.showWarranty &&
                <>
                    <CxGrid size="lg">{ formCheckBox("warranty", "Garantie")}</CxGrid>
                    <CxGrid size="lg" />
                </>
            }

            { cfg.showPump &&
                <>
                    <CxGrid size="lg">{ formCheckBox("pump", "Pumpe")}</CxGrid>
                    <CxGrid size="lg" />
                </>
            }

            { cfg.showMaterialList &&
                <>
                    <CxGrid size="lg">{ formCheckBox("material_list", "Bestellliste")}</CxGrid>
                    <CxGrid size="lg" />
                </>
            }
            { cfg.showClearingList &&
                <>
                    <CxGrid size="lg">{ formCheckBox("clearing_list", "Abräumliste")}</CxGrid>
                    <CxGrid size="lg" />
                </>
            }

            <CxGrid size="lg" justifyContent="flex-end">{ formButtons() }</CxGrid>

            <CxMetaInfo values={formValues} pk="key" />
        </>
    );
}


// --------------------------------------------------------------------------------------------------------------------
// RenderCodeSort
// --------------------------------------------------------------------------------------------------------------------

const RenderCodeSort = (cfg, items) => {

    const {handleAction, currentReturnTo} = useAppCtx();
    const {loadCodes} = useCodeCtx();

    const [sortedItems, setSortedItems] = useState(items.map(item => ({ ...item })));       // clone items
    const [call, setCall] = useState(initialCall())

    const handleOrderChange = (items) => {
        setSortedItems(items)
    }

    const handleAlphabeticalSort = () => {
        const sorted = [...sortedItems]
            .sort((a, b) => a.value.localeCompare(b.value))
            .map((item, index) => ({ ...item, order: index }));
        setSortedItems(sorted);
    };

    const handleSubmit = async (action) => {
        if (action === "cancel") {
            handleAction(currentReturnTo)
        }
        if (action === "save") {
            // save
            setCall(runningCall);
            const responseCall = await codeOrderUpdate(sortedItems);
            if (responseCall.isError) {
                setCall(responseCall)
                return
            } else {
                loadCodes();
            }
            // return to
            handleAction(currentReturnTo)
        }
    };

    return (
        <>
            <CxFormFeedback call={call} />

            <CxGrid size="xl" justifyContent="flex-end">
                <Box display="flex" justifyContent="right" gap={1} sx={{ mt: 2 }}>
                    <CxIconButton label="Alphabetisch sortieren" iconName="abcSort"  onClick={handleAlphabeticalSort} />
                </Box>
            </CxGrid>

            <CxGrid size="xl">
                <CxCodeList
                    items={sortedItems}
                    enableEdit={false}
                    onEditChange={null}
                    enableOrder={true}
                    onOrderChange={handleOrderChange}
                    showQuantity={cfg.showQuantity}
                    showWarranty={cfg.showWarranty}
                />
            </CxGrid>

            <CxGrid size="xl" justifyContent="flex-end">
                <CxCancelSaveButton handleSubmit={handleSubmit} />
            </CxGrid>
        </>
    );
}


// --------------------------------------------------------------------------------------------------------------------
// Create, Update and Sort
// --------------------------------------------------------------------------------------------------------------------

export function CodeCreate() {
    const { currentData } = useAppCtx();

    const cfg = currentData.cfg;
    const items = currentData.items;
    const title = cfg.singularLabel + " erstellen";

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                <PageHeader title={title} />
                { RenderCode(cfg, items) }
            </CxGrid>
        </Box>
    );
}

export function CodeUpdate() {
    const { currentData } = useAppCtx();

    const cfg = currentData.cfg;
    const items = currentData.items;
    const item = currentData.item;
    const title = cfg.singularLabel + " bearbeiten";

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                <PageHeader title={title} />
                { RenderCode(cfg, items, item) }
            </CxGrid>
        </Box>
    );
}

export function CodeSort() {
    const { currentData } = useAppCtx();

    const cfg = currentData.cfg;
    const items = currentData.items;
    const title = cfg.label + " sortieren";

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                <PageHeader title={title} />
                <CxGrid size="xl">
                    <CxText>
                        Mit den Auf- und Ab-Pfeilen kann die Reihenfolge für die Anzeige in den Auswahlboxen einzein geändert werden.
                        Mit [Alphabetisch sortieren] werden alle Einträge sortiert. Wichtig: Bei der Auswahl erscheinen nur die aktiven Einträge.
                    </CxText>
                </CxGrid>
                { RenderCodeSort(cfg,items) }
            </CxGrid>
        </Box>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// The end.